import type { PostHog } from "posthog-js";
import type { AuthUser } from "../auth/AuthProvider";

// Set once the dynamic import resolves and posthog.init() has run. identify/
// reset read this rather than window.posthog so they stay typed.
let posthog: PostHog | null = null;
// Resolves when init has finished (or immediately, if PostHog is disabled).
// Consumers await this so the first identify isn't dropped while posthog-js is
// still loading.
let posthogReady: Promise<void> = Promise.resolve();

/**
 * Initialize PostHog product analytics.
 *
 * Guarded so it only runs once, only in production, and only when a key is
 * configured. The dynamic import keeps posthog-js out of the main bundle in
 * non-production builds. SPA pageviews are auto-captured via the History API
 * by the `defaults` preset — no manual useLocation wiring needed.
 */
export function initPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (
    !key ||
    import.meta.env.VITE_PORTAL_ENV !== "production" ||
    (window as unknown as { posthog?: unknown }).posthog
  ) {
    return;
  }

  posthogReady = import("posthog-js").then(({ default: client }) => {
    client.init(key, {
      api_host: import.meta.env.VITE_POSTHOG_HOST,
      // Proxy host serves ingestion; ui_host points in-app links back to
      // PostHog's own dashboard rather than the proxy domain.
      ui_host: "https://us.posthog.com",
      defaults: "2026-05-30",
      // Only create person profiles for identified (logged-in) users — the
      // portal authenticates everyone and calls identify(), so anonymous
      // profiles would just be noise.
      person_profiles: "identified_only",
    });
    posthog = client;
  });
}

/**
 * Resolves once PostHog has finished initializing. Resolves immediately when
 * PostHog is disabled (non-production or no key), in which case `posthog`
 * stays null and identify/reset no-op.
 */
export function whenPostHogReady(): Promise<void> {
  return posthogReady;
}

/**
 * Identify the current user to PostHog.
 *
 * When the user is impersonating another account, we identify as the
 * impersonated user and attach `impersonated_by` / `impersonated_by_email`
 * properties so admin activity is distinguishable in analytics.
 */
export function identifyUser(user: AuthUser): void {
  if (!posthog) return;

  if (user.impersonating) {
    posthog.identify(user.impersonating.id, {
      email: user.impersonating.email,
      tenant_id: user.impersonating.tenant_id,
      tenant_name: user.impersonating.tenant?.name,
      impersonated_by: user.id,
      impersonated_by_email: user.email,
    });
  } else {
    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      tenant_id: user.tenant_id,
      tenant_name: user.tenant?.name,
      roles: user.roles,
      is_superadmin: user.is_superadmin,
    });
  }
}

/**
 * Reset PostHog identity on logout so the next session starts anonymous.
 */
export function resetUser(): void {
  if (!posthog) return;

  posthog.reset();
}
